import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../../lib/api'
import { EmergencyDisclaimer } from '../../components/EmergencyDisclaimer'
import { CLINICAL_CONCERNS } from '../../lib/vocab'
import type { ProviderProfile as ProviderProfileType } from '../../lib/types'

function initials(name: string): string {
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase()
}

function specialtyLabel(value: string): string {
  return CLINICAL_CONCERNS.find((c) => c.value === value)?.label || value
}

export default function ProviderProfile() {
  const { providerId } = useParams<{ providerId: string }>()
  const navigate = useNavigate()
  const [provider, setProvider] = useState<ProviderProfileType | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api(`/patients/me/providers/${providerId}/profile`)
      .then(setProvider)
      .catch(() => setError('Could not load this provider — they may no longer be accepting new patients.'))
      .finally(() => setLoading(false))
  }, [providerId])

  if (loading) return <div className="page-loading">Loading provider profile…</div>
  if (error || !provider) return <div className="form-error">{error || 'Provider not found.'}</div>

  const priceLabel = provider.self_pay_price_min_cents != null || provider.self_pay_price_max_cents != null
    ? `$${((provider.self_pay_price_min_cents ?? provider.self_pay_price_max_cents)! / 100).toFixed(0)}–${((provider.self_pay_price_max_cents ?? provider.self_pay_price_min_cents)! / 100).toFixed(0)} / session${provider.sliding_scale_available ? ' (sliding scale available)' : ''}`
    : provider.self_pay_price_cents != null ? `$${(provider.self_pay_price_cents / 100).toFixed(0)} / session` : null
  const location = [provider.city, provider.state_province, provider.country].filter(Boolean).join(', ')
  const availabilityLabel = provider.virtual_only
    ? 'Virtual only'
    : provider.session_format === 'either'
      ? 'Virtual or in-person'
      : provider.session_format === 'virtual'
        ? 'Virtual'
        : provider.session_format === 'in_person'
          ? 'In-person'
          : null

  return (
    <div>
      <div className="page-head">
        <button className="btn small outline" onClick={() => navigate(-1)} style={{ marginBottom: 10 }}>← Back</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h1 style={{ margin: 0 }}>{provider.name}</h1>
          <span className={`badge ${provider.credentialing_display === 'Verified' ? 'badge-green' : 'badge-amber'}`}>{provider.credentialing_display}</span>
        </div>
        {provider.credentials && <div className="ph-sub">{provider.credentials}</div>}
      </div>
      <EmergencyDisclaimer />

      <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {provider.photo_url ? (
          <img src={provider.photo_url} alt={provider.name} style={{ width: 88, height: 88, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <div style={{
            width: 88, height: 88, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', background: 'var(--warm-terracotta, #C56A4B)', color: 'white', fontSize: 28, fontWeight: 600,
          }}>
            {initials(provider.name)}
          </div>
        )}
        <div style={{ flex: 1 }}>
          {provider.bio && <p style={{ marginTop: 0 }}>{provider.bio}</p>}
          <div className="row-list">
            {location && <div className="row"><span>Location</span><span className="muted">{location}</span></div>}
            {availabilityLabel && <div className="row"><span>Availability</span><span className="muted">{availabilityLabel}</span></div>}
            {priceLabel && <div className="row"><span>Self-pay rate</span><span className="muted">{priceLabel}</span></div>}
            <div className="row"><span>Accepting new patients</span><span className="muted">{provider.accepting_new_clients ? 'Yes' : 'No'}</span></div>
          </div>
        </div>
      </div>

      {provider.specialties.length > 0 && (
        <div className="card" style={{ marginTop: 12 }}>
          <h3>Specialties</h3>
          <div className="chip-list">
            {provider.specialties.map((s) => <span key={s} className="chip">{specialtyLabel(s)}</span>)}
          </div>
        </div>
      )}

      {provider.modalities.length > 0 && (
        <div className="card" style={{ marginTop: 12 }}>
          <h3>Treatment approaches</h3>
          <div className="chip-list">
            {provider.modalities.map((m) => <span key={m} className="chip">{m}</span>)}
          </div>
        </div>
      )}

      {provider.languages.length > 0 && (
        <div className="card" style={{ marginTop: 12 }}>
          <h3>Languages</h3>
          <div className="chip-list">
            {provider.languages.map((l) => <span key={l} className="chip">{l}</span>)}
          </div>
        </div>
      )}

      {(provider.insurance_types_accepted.length > 0 || provider.insurance_accepted.length > 0) && (
        <div className="card" style={{ marginTop: 12 }}>
          <h3>Insurance accepted</h3>
          <div className="chip-list">
            {[...provider.insurance_types_accepted, ...provider.insurance_accepted].map((i) => <span key={i} className="chip">{i.replace(/_/g, ' ')}</span>)}
          </div>
        </div>
      )}

      <button className="btn full" style={{ marginTop: 16 }} onClick={() => navigate(`/patient/book/${provider.id}`)}>
        Book Appointment
      </button>
    </div>
  )
}
